import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  AllInclusive as InfinityIcon
} from '@mui/icons-material';

const ViewPlan = ({ plan }) => {
  if (!plan) {
    return <Typography>No plan data available</Typography>;
  }

  const formatPrice = (price) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(price);

  const formatDuration = (days) => {
    if (days < 30) return `${days} days`;
    if (days === 30) return '1 month';
    if (days < 365) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;
      return remainingDays > 0 ? `${months} months ${remainingDays} days` : `${months} months`;
    }
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    return remainingDays > 0 ? `${years} years ${remainingDays} days` : `${years} years`;
  };

  const getPermission = (slug) => plan.permissions?.find((permission) => permission.slug === slug);
  const contactLimit = plan.contact_view_limit ?? getPermission('view_contact_details')?.limit ?? 0;
  const officeProfiles = plan.office_assisted_profiles ?? getPermission('office_assisted_profiles')?.limit ?? 0;
  const hasUnlimitedChat = plan.unlimited_chat ?? Boolean(getPermission('chat_messages')?.is_allowed);

  const benefits = [
    {
      label: 'Contact Details Views',
      value: `${contactLimit} profiles`,
      enabled: contactLimit > 0
    },
    {
      label: 'Chat',
      value: hasUnlimitedChat ? 'Unlimited' : 'Not included',
      enabled: hasUnlimitedChat,
      unlimited: hasUnlimitedChat
    },
    {
      label: 'Office Suggested Profiles',
      value: officeProfiles > 0 ? `${officeProfiles} profiles` : 'Not included',
      enabled: officeProfiles > 0
    }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {formatPrice(plan.price)}
                  </Typography>
                  {plan.description && (
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      {plan.description}
                    </Typography>
                  )}
                  <Typography variant="body1" color="text.secondary">
                    Validity: {formatDuration(plan.duration_days)}
                  </Typography>
                </Box>
                <Chip
                  label={plan.is_active ? 'Active' : 'Inactive'}
                  color={plan.is_active ? 'success' : 'error'}
                  variant="filled"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Contact Views</Typography>
                  <Typography variant="h6">{contactLimit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Office Suggestions</Typography>
                  <Typography variant="h6">{officeProfiles}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Enrolled Users</Typography>
                  <Typography variant="h6">{plan.enrolledUsers || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{new Date(plan.createdAt).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Benefits Overview
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {benefits.map((benefit) => (
                  <Chip
                    key={benefit.label}
                    icon={benefit.unlimited ? <InfinityIcon /> : <CheckIcon />}
                    label={`${benefit.label}: ${benefit.value}`}
                    color={benefit.enabled ? 'success' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Detailed Access
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Limit</TableCell>
                  <TableCell>Usage Window</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {benefits.map((benefit) => (
                  <TableRow key={benefit.label}>
                    <TableCell>
                      <Typography variant="subtitle2">{benefit.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={benefit.enabled ? 'Included' : 'Not Included'}
                        color={benefit.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{benefit.value}</TableCell>
                    <TableCell>Total plan validity</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewPlan;